// =============================================================
// quickNoteContext — "what record is on screen", for Quick Note.
//
// iOS Quick Note offers to link a note to whatever you were looking at. Pages
// that show one record publish it here (useQuickNoteContext); the newest one
// still mounted wins, so a task opened over a deal page is the context until it
// closes, then the deal is again. Nothing is linked automatically — linking
// shares the note with that record's team, so Quick Note only OFFERS it.
// =============================================================

import { useEffect, useState } from 'react';

let stack = [];
const subs = new Set();
const emit = () => { const top = stack[stack.length - 1]?.ref || null; subs.forEach(fn => fn(top)); };

export function currentQuickNoteContext() { return stack[stack.length - 1]?.ref || null; }

/** Publish `ref` ({ type: 'Deal'|'DealDetail'|'Task', id, label }) while the caller is mounted. */
export function usePublishQuickNoteContext(ref) {
  const key = ref ? `${ref.type}:${ref.id}:${ref.label || ''}` : '';
  useEffect(() => {
    if (!ref?.id) return undefined;
    const token = {};
    stack.push({ token, ref: { type: ref.type, id: ref.id, label: ref.label || null } });
    emit();
    return () => { stack = stack.filter(s => s.token !== token); emit(); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useQuickNoteContext() {
  const [ctx, setCtx] = useState(currentQuickNoteContext);
  useEffect(() => { subs.add(setCtx); return () => { subs.delete(setCtx); }; }, []);
  return ctx;
}

/** Ctrl/⌘ + Alt + N — by key position, since Alt changes the character typed. */
export const isQuickNoteShortcut = (e) => !!(e.altKey && (e.ctrlKey || e.metaKey) && !e.shiftKey && e.code === 'KeyN');
